import type HlsType from 'hls.js'

/**
 * 動画要素にHLS(またはMP4フォールバック)ソースを接続する。
 *
 * - ネイティブHLS対応(Safari/iOS): video.src に直接HLS URLを設定。
 * - 非対応(Chrome/Firefox等): hls.js を **セルフホストのscriptタグで遅延ロード**して attach。
 *   バンドルには含めないため、埋め込み(embedded.js)の基底サイズを増やさない。
 * - hls.js が致命的エラー、未サポート、ロード失敗: MP4 にフォールバック。
 *
 * 戻り値は後始末関数(hls.js インスタンスの破棄)。hlsUrl が無いときは呼ばない。
 */

type HlsConstructor = typeof HlsType

type WindowWithHls = Window & {
  Hls?: HlsConstructor
  // 埋め込み(他社サイト)では studio のオリジンをここに設定する(embedded.tsx が設定)。
  // 未設定(moovy 自身のアプリ)では相対パスで同一オリジンから読む。
  __MOOVY_ASSET_ORIGIN__?: string
}

const HLS_MIME = 'application/vnd.apple.mpegurl'

let hlsLoadPromise: Promise<HlsConstructor | null> | null = null

/**
 * hls.js を自己ホスト(/scripts/hls.min.js)からscriptタグで遅延ロードし、
 * グローバルの Hls コンストラクタを返す。ロード不可なら null。
 */
export function loadHls(): Promise<HlsConstructor | null> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve(null)
  }
  const w = window as WindowWithHls
  if (w.Hls) {
    return Promise.resolve(w.Hls)
  }
  if (hlsLoadPromise) {
    return hlsLoadPromise
  }
  hlsLoadPromise = new Promise((resolve) => {
    const origin = w.__MOOVY_ASSET_ORIGIN__ ?? ''
    const script = document.createElement('script')
    script.src = `${origin}/scripts/hls.min.js`
    script.async = true
    script.onload = () => resolve(w.Hls ?? null)
    script.onerror = () => {
      hlsLoadPromise = null
      resolve(null)
    }
    document.head.appendChild(script)
  })
  return hlsLoadPromise
}

export type AttachHlsParams = {
  hlsUrl: string
  /** フォールバック用のMP4 URL(署名付き)。null の場合フォールバックしない */
  mp4Url: string | null
  /** この動画は読み込み完了後に自動再生する想定か。MP4フォールバックの再生継続判定に使う */
  autoPlay?: boolean
}

/**
 * hls.destroy() で失われる再生状態のスナップショット。
 * destroy() は MediaSource を外すので、その瞬間に currentTime と played がリセットされる。
 * 復元に使う値は必ず destroy() より前に控えること。
 */
type PlaybackSnapshot = {
  resumeTime: number
  wasPlaying: boolean
  hasStarted: boolean
}

export function attachHlsSource(video: HTMLVideoElement, { hlsUrl, mp4Url, autoPlay }: AttachHlsParams): () => void {
  const nativeSupport = video.canPlayType(HLS_MIME)

  // ネイティブHLSに任せるのは 'probably'（Safari/iOS）だけ。
  // Chrome は HLS を再生できないのに 'maybe' を返す（Chrome 152 で確認）。
  // ここで src に入れてしまうと readyState 0 のまま止まり、再生が始まらない。
  if (nativeSupport === 'probably') {
    video.src = hlsUrl
    return () => {}
  }

  let destroyed = false
  let hlsInstance: { destroy: () => void } | null = null

  /**
   * MP4 へ差し替える。src の再設定は HTML 仕様の media load algorithm を走らせ、
   * その中で autoplaying flag が true に戻る = `autoplay` 属性が再武装される。
   * そのため差し替え前の再生位置と再生/停止状態を引き継がないと、視聴者が
   * 一時停止した動画が 0:00 から勝手に再生される（別タブから戻った時など、
   * バックグラウンドで hls.js が致命的エラーに落ちた場合に起きる）。
   */
  const fallbackToMp4 = (snapshot?: PlaybackSnapshot) => {
    if (!mp4Url) return

    // destroy() 済みの video から読むと currentTime も played も 0 になっているため、
    // 呼び出し側が destroy 前に控えた snapshot を優先する。snapshot が無いのは
    // hls.js 非対応環境での初回フォールバックで、その場合は実時間を読んで問題ない。
    const { resumeTime, wasPlaying, hasStarted } = snapshot ?? {
      resumeTime: video.currentTime,
      wasPlaying: !video.paused,
      // 一度でも再生が始まっていれば「視聴者が操作した結果の停止」とみなす
      hasStarted: video.played.length > 0,
    }

    video.src = mp4Url

    const restoreState = () => {
      if (resumeTime > 0) {
        video.currentTime = resumeTime
      }
      if (wasPlaying || (!hasStarted && autoPlay)) {
        // 再生中の差し替え、または初回ロード中に落ちたケースは再生を続ける
        void video.play().catch(() => undefined)
      } else {
        // 視聴者が止めていた動画は止めたまま（pause で autoplay の再発火も打ち消す）
        video.pause()
      }
    }

    if (video.readyState >= 1) {
      restoreState()
    } else {
      video.addEventListener('loadedmetadata', restoreState, { once: true })
    }
  }

  loadHls().then((Hls) => {
    if (destroyed) {
      return
    }
    if (!Hls || !Hls.isSupported()) {
      // hls.js が使えない環境（MSE非対応の iOS Safari など）で 'maybe' を返すなら、
      // ネイティブHLSに任せる。MP4フォールバックより帯域を食わない。
      if (nativeSupport) {
        video.src = hlsUrl
        return
      }
      fallbackToMp4()
      return
    }
    const hls = new Hls()
    hlsInstance = hls
    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (data.fatal) {
        // destroy() は MediaSource を外すため、呼んだ瞬間に currentTime と played が
        // 0 に戻る。復元に使う値は必ず destroy() より先に控える
        // （CVで別タブへ移って戻った動画が 0:00 になっていた原因）。
        const snapshot: PlaybackSnapshot = {
          resumeTime: video.currentTime,
          wasPlaying: !video.paused,
          hasStarted: video.played.length > 0,
        }
        hls.destroy()
        hlsInstance = null
        fallbackToMp4(snapshot)
      }
    })
    hls.loadSource(hlsUrl)
    hls.attachMedia(video)
  })

  return () => {
    destroyed = true
    if (hlsInstance) {
      hlsInstance.destroy()
      hlsInstance = null
    }
  }
}
